import { Injectable, signal, inject } from '@angular/core';
import { forkJoin, firstValueFrom, of, filter } from 'rxjs';
import { QualificaService } from './api/qualifica.service';
import { DocumentoService } from './api/documento.service';
import { ContrattoService } from './api/contratto.service';
import { AuthService } from './auth.service';
import { Role } from '../models/role.enum';
import { NavigationEnd, Router } from '@angular/router';

export interface NeadbadgeState {
    contratti: any;
    documenti: any;
    qualifiche: any;
}

@Injectable({
    providedIn: 'root'
})
export class NeadbadgeService {

    private readonly qualificaService = inject(QualificaService);
    private readonly documentoService = inject(DocumentoService);
    private readonly contrattoService = inject(ContrattoService);
    private readonly authService = inject(AuthService);
    private readonly router = inject(Router);
    private readonly monitoringStarted = signal<boolean>(false);
    private readonly enabledRefreshBadgePaths = [
        '/dashboard', 
        '/contratti', 
        '/documenti', 
        '/qualifica'
    ];

    // Metodo da chiamare solo dopo il login successo
    startMonitoring(): void {
        if (this.monitoringStarted()) return; // Evita sottoscrizioni multiple
        this.monitoringStarted.set(true);
        console.log('[NeadbadgeService] Avvio monitoraggio post-auth...');
        
        // Carichiamo i dati la prima volta appena loggati
        this.loadAllInitialData();

        // Poi restiamo in ascolto dei cambi pagina
        this.router.events.pipe(
            filter((event): event is NavigationEnd => event instanceof NavigationEnd)
        ).subscribe((event: NavigationEnd) => {
            const currentPath = event.urlAfterRedirects.split('?')[0];
            this.checkPath(currentPath);
        });
    }

    private checkPath(url: string) {
        if (this.enabledRefreshBadgePaths.includes(url.split('?')[0])) {
            console.log(`Path ${url} abilitato: aggiorno tutti i badge...`);
            this.loadAllInitialData();
        }
    }

    // Stato interno privato (Signal)
    private readonly state = signal<NeadbadgeState>({
        contratti: 0,
        documenti: 0,
        qualifiche: 0
    });

    readonly badgeData = this.state.asReadonly();

    getRequests(): any {
        let req = {
            contratti: of(0),
            documenti: of(0),
            qualifiche: of(0)
        };
        if (this.authService.ROLE_ADMIN_ROLES() || this.authService.hasRole(Role.OPERATORE)) {
            req = {
                contratti: this.contrattoService.countNear(),
                documenti: this.documentoService.countNear(),
                qualifiche: this.qualificaService.countNear()
            };
        }
        else if (this.authService.hasRole(Role.FORNITORE)) {
            req = {
                ...req,
                documenti: this.documentoService.countNear(),
                qualifiche: this.qualificaService.countNear()
            };
        }
        return req;
    }

    async loadAllInitialData() {
        const requests = this.getRequests();
        if (requests) {
            try {
                const results = await firstValueFrom(forkJoin(requests));
                this.state.set(results as NeadbadgeState);
            } catch (error) {
                console.error('Errore nel caricamento parallelo Neadbadge:', error);
            }
        }

    }


    getCallForKey(key: string) {
        switch (key) {
            case 'contratti': return this.contrattoService.countNear();
            case 'documenti': return this.documentoService.countNear();
            case 'qualifiche': return this.qualificaService.countNear();
            default: return of(null);
        }
    }

    /**
     * Aggiorna una singola chiave specifica dell'oggetto
     * @param key La chiave da aggiornare (users, inventory, etc.)
     */
    async refreshSingleKey(key: keyof NeadbadgeState) {
        try {
            // Effettua la chiamata singola
            const result = await firstValueFrom(this.getCallForKey(key));

            // Aggiorna solo la porzione interessata dello stato
            this.state.update(currentState => ({
                ...currentState,
                [key]: result
            }));
        } catch (error) {
            console.error(`Errore durante il refresh di ${key}:`, error);
        }
    }


    async refreshContratti() { await this.refreshSingleKey('contratti'); }
    async refreshDocumenti() { await this.refreshSingleKey('documenti'); }
    async refreshQualifiche() { await this.refreshSingleKey('qualifiche'); }




}